<template>
	<div
		class="tag-list"
		:class="{ 'is-mobile': isMobileWebApp }"
	>
		<FilterableList
			:title="transUcfirst('analysis.tag_list')"
			class="tag-list__inner-w"
			:subtitle="selectedTagGroup.name"
			:active-filters="activeFiltersComp"
			:filter-no-selection-label="filtersLabel"
			:filters-module="filtersModuleComp"
			:hide-title-visibility="isSearchActive"
			header-padding-horizontal="md"
			header-padding-vertical="md"
			modifier="tag-list"
			@filters-updated="updateAndApplyActiveFilters"
		>
			<template #over-title>
				<div
					v-if="isSearchActive"
					class="tag-list__search"
				>
					<ClipSearchInput
						:placeholder="transUcfirst('analysis.clip_search.search_in_tag_list')"
						:search-keyword="searchKeyword"
						@close="closeOrClearSearch"
						@change="onSearchInput"
					/>
				</div>
			</template>
			<template #filters-dropdown="{ filtersModule }">
				<div class="tag-list__actions">
					<SearchButton
						v-if="isSearchBtnVisible && !isSearchActive"
						@click="isSearchActive = true"
					/>
					<SortButton
						:sort-dir="sortDir"
						@sort-updated="handleSortUpdated"
					/>
					<ClipFilters
						:title="filtersListTitle"
						:module-name="filtersModule"
						:show-filters-count="isMobileWebApp"
						:search-keyword="searchKeyword"
						@filters-updated="updateAndApplyActiveFilters"
					/>
				</div>
			</template>
			<template #list-view>
				<!-- single sport -->
				<RaceSportTagList
					v-if="isSequentialCompetition"
					:event-target-tags="eventItems"
					:athlete-target-tags="athleteItems"
					:selected-items="selectedItems"
					:sort-dir="sortDir"
					:active-tab="activeTab"
					@selection-changed="handleSelectionChanged"
					@row-click="handleRowClick"
					@tab-change="handleTabChange"
				/>
				<!-- team sport -->
				<TagGrid
					v-else
					:enable-recycler-view="true"
					:recycler-page-size="25"
					:items="items"
					:selected-items="selectedItems"
					:sport-name="sportName"
					:can-select="true"
					:swipable="isMobileWebApp"
					:swipe-left-options="{ icon: 'network-communication', action: handleShare }"
					:swipe-right-options="{ icon: 'bin', action: handleDelete }"
					:checkbox-always-visible="isMobileWebApp"
					@row-click="handleRowClick"
					@selection-changed="handleSelectionChanged"
				>
					<template #no-results>
						<NoResults
							:title="emptyMsg.title"
							:description="emptyMsg.description"
						/>
					</template>
				</TagGrid>
			</template>
			<template #footer>
				<div class="tag-list__footer">
					<RowActions
						v-if="hasSelection"
						:selected="selectedItems"
						:total="tags.length"
						@edit="handleMultipleEdit"
						@delete="handleMultipleDelete"
						@close="clearSelection"
					/>
				</div>
			</template>
		</FilterableList>

		<ShareModal
			v-if="isShareModalOpen"
			:title="shareTagModalTitle"
			:item="shareTag"
			:item-label="transTagLabel(shareTag.name, event.sportName, shareTag.tag ? shareTag.tag.type : null)"
			:url-label="urlLabel"
			:url="shareUrl"
			:links="shareLinks"
			@close="closeShareModal"
		/>
	</div>
</template>

<script>
/* eslint-disable vue/one-component-per-file */
import bus from '@videotools/js/events';
import { mapActions, mapGetters } from 'vuex';
import FilterableList from '@components/lists/FilterableList';
import ShareModal from '@components/share-modal/ShareModal';
import TagGrid from './TagGrid';
import RowActions from '../RowActions/RowActions';
import ClipFilters from '@components/clip-list/ClipFilters';
import SortButton from '@components/clip-list/SortButton';
import RaceSportTagList from '@videotools/js/analysis/components/TagList/RaceSportTagList';
import { TAB_ATHLETE_TAGS, TAB_EVENT_TAGS } from '@videotools/js/constants/athletes';
import ClipSearchInput from '@components/search/ClipSearchInput';
import SearchButton from '@components/search/SearchButton';
import NoResults from '@videotools/js/analysis/components/TagList/NoResults';
import { FILTER_GROUP_TAG } from '@js/store/modules/clip-filters';
import { TAG_NAMES, TAG_TYPE_CLOCK } from '@js/api/entities/TagButton';

export default {
	name:       'TagList',
	components: {
		NoResults,
		SearchButton,
		ClipSearchInput,
		RaceSportTagList,
		SortButton,
		ClipFilters,
		RowActions,
		TagGrid,
		ShareModal,
		FilterableList,
	},
	data() {
		return {
			filtersKeyPrefix:   'taglist',
			isShareModalOpen:   false,
			shareTag:           {},
			shareLinks:         null,
			shareUrl:           null,
			tags:               [],
			selectedItems:      [],
			sortDir:            'desc',
			isSearchActive:     false,
			isSearchBtnVisible: true,
		};
	},
	computed: {
		...mapGetters({
			activeTab:                 'app/sequentialCompetitionTagListActiveTab',
			isMobileWebApp:            'app/isMobileWebApp',
			isTouchDevice:             'device/isTouchDevice',
			supportsNativeShare:       'device/supportsNativeShare',
			event:                     'event/event',
			sportName:                 'event/sportName',
			eventTags:                 'event/tags',
			isSequentialCompetition:   'event/isSequentialCompetition',
			eventTagsForEvent:         'event/eventTagsForEvent',
			eventTagsForParticipant:   'event/eventTagsForParticipant',
			selectedTagGroup:          'tagGroups/selectedTagGroup',
			activeFilters:             'tagFilters/activeFilters',
			searchKeyWordStore:        'tagFilters/searchKeyword',
			sorting:                   'tags/sorting',
			canEditEventTag:           'permissions/canEditEventTag',
			activeAthleteFilters:      'athleteTagFilters/activeFilters',
			searchKeyWordStoreAthlete: 'athleteTagFilters/searchKeyword',
		}),
		items() {
			return [...this.eventItems, ...this.athleteItems];
		},
		athleteItems() {
			if (!this.isSequentialCompetition) {
				return [];
			}

			const evTags = this.$store.getters['athleteTagFilters/filteredClips'];
			return this.sortTags(evTags, this.sortDir).map((tag) => ({
				id:              tag.id,
				tag,
				name:            tag.name,
				time:            tag.formattedTimestamp,
				duration:        tag.formattedDuration,
				imageUrl:        tag.imageUrl,
				player:          tag.player,
				team:            tag.teamName,
				timestamp:       tag.timestamp,
				participantName: tag.participantName,
				action:          tag.name,
				disabled:        !this.canEditEventTag(tag),
				gameMinute:      tag.gameTime?.gameMinute,
				tooltip:         tag.isOutsideVideo ? this.$I18n.trans('analysis.tag_outside_video') : null,
				rating:          tag.rating,
			}));
		},
		eventItems() {
			const evTags = this.$store.getters['tagFilters/filteredClips'];
			const items = this.sortTags(evTags, this.sortDir).map((tag) => ({
				id:              tag.id,
				tag,
				name:            tag.name,
				time:            tag.formattedTimestamp,
				duration:        tag.formattedDuration,
				durationSeconds: tag.duration,
				imageUrl:        tag.imageUrl,
				player:          tag.player,
				team:            tag.teamName,
				timestamp:       tag.timestamp,
				participantName: tag.participantName,
				action:          tag.name,
				gameMinute:      tag.gameTime?.gameMinute,
				tooltip:         tag.isOutsideVideo ? this.$I18n.trans('analysis.tag_outside_video') : null,
				isOutsideVideo:  tag.isOutsideVideo,
				rating:          tag.rating,
			}));

			// exclude clock tags if no clock filters are set
			if (!this.activeFilters.some(filter =>
				filter.type === FILTER_GROUP_TAG && TAG_NAMES[TAG_TYPE_CLOCK].includes(filter.name)
			)) {
				return items.filter(item => !TAG_NAMES[TAG_TYPE_CLOCK].includes(item.tag.name));
			}

			return items;
		},
		filtersModuleComp() {
			if (!this.isSequentialCompetition) {
				return 'tagFilters';
			}

			return this.activeTab === null || this.activeTab === TAB_EVENT_TAGS ? 'tagFilters' : 'athleteTagFilters';
		},
		activeFiltersComp() {
			if (!this.isSequentialCompetition) {
				return this.activeFilters;
			}
			return this.activeTab === null || this.activeTab === TAB_EVENT_TAGS ? this.activeFilters : this.activeAthleteFilters;
		},
		shareTagModalTitle() {
			if (!this.$I18n) return 'Share Tag';
			return this.$I18n.trans('commons.type_share', {
				type: this.$I18n.trans_choice('analysis.tag', 1),
			});
		},
		urlLabel() {
			return this.$I18n ? this.$I18n.trans('analysis.tag_url') : 'Tag-URL';
		},
		filtersLabel() {
			if (!this.$I18n) return 'All Tags';
			return this.$I18n.trans('commons.all_types', {
				type: this.transChoiceUcFirst('analysis.tag', 2),
			});
		},
		hasSelection() {
			return this.selectedItems.length > 0;
		},
		selectionSize() {
			return this.selectedItems.length;
		},
		filtersListTitle() {
			return `${this.transUcfirst('commons.filter')} ${this.transChoiceUcFirst('commons.tag', 2)}`;
		},
		searchKeyword: {
			get() {
				if (this.isSequentialCompetition && this.activeTab === TAB_ATHLETE_TAGS) {
					return this.searchKeyWordStoreAthlete;
				}
				return this.searchKeyWordStore;
			},
			set() {},
		},
		emptyMsg() {
			if (this.searchKeyword?.length) {
				return {
					title:       this.transUcfirst('analysis.clip_search.no_results_for', { keyword: this.searchKeyword }),
					description: this.transUcfirst('analysis.clip_search.no_results_text'),
				};
			}

			return { title: this.$I18n.trans('commons.no_types_found', { type: this.$I18n.trans_choice('commons.tag', 2) }) };
		},
	},
	watch: {
		eventTags() {
			this.initEventTags();
		},
		hasSelection(value) {
			// Hide the popup menu on mobile if there are selected items and show it otherwise
			if (this.isMobileWebApp) {
				if (value) bus.publish('tabs:hide-menu');
				else bus.publish('tabs:show-menu');
			}
		},
		activeTab() {
			this.isSearchActive = this.searchKeyword?.length > 0;
		},
	},
	created() {
		if (this.sorting.TagList) this.sortDir = this.sorting.TagList.direction;
		this.initEventTags();

		if (this.isSequentialCompetition && this.activeTab === null) {
			this.setActiveTab(TAB_EVENT_TAGS);
		}

		this.isSearchActive = this.searchKeyword?.length > 0;
		this.isSearchBtnVisible = !this.isSearchActive;
	},
	methods: {
		...mapActions({
			setActiveTab:               'app/setSequentialCompetitionTagListActiveTab',
			updateAthleteActiveFilters: 'athleteTagFilters/updateActiveFilters',
			setSourceTagsAthlete:       'athleteTagFilters/setSourceTags',
			searchAthleteClips:         'athleteTagFilters/searchClips',
			clearAthleteSearch:         'athleteTagFilters/clearSearch',
			shareNative:                'device/shareNative',
			deleteEventTags:            'event/deleteEventTags',
			editEventTag:               'eventTagEdit/editEventTag',
			bulkEditEventTags:          'eventTagEdit/bulkEditEventTags',
			updateActiveFilters:        'tagFilters/updateActiveFilters',
			setSourceTagsEvent:         'tagFilters/setSourceTags',
			searchClips:                'tagFilters/searchClips',
			clearSearch:                'tagFilters/clearSearch',
			setSorting:                 'tags/setSorting',
			setTimelineActiveTags:      'timelineTags/setActiveTags',
		}),
		initEventTags() {
			if (this.isSequentialCompetition) {
				this.setSourceTagsEvent(this.eventTagsForEvent);
				this.setSourceTagsAthlete(this.eventTagsForParticipant);
			}
			this.updateTimelineTags();
		},
		async updateAndApplyActiveFilters(filters) {
			if (this.isSequentialCompetition && this.activeTab === TAB_ATHLETE_TAGS) {
				await this.updateAthleteActiveFilters(filters);
			} else {
				await this.updateActiveFilters(filters);
			}
			this.updateTimelineTags();
		},
		handleSelectionChanged(selectedItems) {
			this.selectedItems = selectedItems;
		},
		handleDelete(item) {
			this.handleMultipleDelete([item]);
		},
		handleEdit(item) {
			this.$emit('edit', [item]);
		},
		handleMultipleEdit(items) {
			if (items.length === 1) {
				this.editEventTag(items[0].tag);
			} else {
				this.bulkEditEventTags(items.map(item => item.tag));
			}
		},
		async handleMultipleDelete(tags) {
			try {
				await this.$confirmDelete({
					title: this.$I18n.trans('commons.type_delete', {
						type: this.$I18n.trans_choice('analysis.tag', tags.length),
					}),
					body:   this.$I18n.trans('commons.delete_confirmation'),
					target: `${tags.length} ${this.$I18n.trans_choice('analysis.tag', tags.length).toLowerCase()}`,
				});
				await this.deleteEventTags(tags);
				this.clearSelection();
				// this.updateFilteredList();
			} catch {}
		},
		handleShare(itemOrItems) {
			let tag;
			if (Array.isArray(itemOrItems)) {
				tag = itemOrItems[0].tag;
			} else {
				tag = itemOrItems.tag;
			}

			this.openShareModal(tag);
		},
		handlePlay(item) {
			this.$emit('play', item);
		},
		handleRowClick(item) {
			this.handleEdit(item);
		},
		openShareModal(tag) {
			this.shareTag = tag;
			this.shareUrl = this.shareTag.url;
			this.shareLinks = this.shareTag.shareLinks;
			if (this.isTouchDevice && this.supportsNativeShare) {
				this.shareNative({
					url: this.shareUrl,
				});
			} else {
				this.isShareModalOpen = true;
			}
		},
		closeShareModal() {
			this.isShareModalOpen = false;
		},
		clearSelection() {
			this.selectedItems = [];
		},
		handleSortUpdated(attribute, dir) {
			this.setSorting({
				component: 'TagList',
				key:       attribute,
				direction: dir,
			});
			this.sortDir = dir;
		},
		sortTags(tags, dir) {
			const ret = dir === 'asc' ? -1 : 1;
			tags.sort((tagA, tagB) => {
				return tagA.timestamp > tagB.timestamp ? ret : -ret;
			});
			return tags;
		},
		handleTabChange(tab) {
			this.setActiveTab(tab);
			this.updateTimelineTags();
		},
		updateTimelineTags() {
			const isFiltered = this.$store.getters['tagFilters/isFiltered'];
			let tags = null;
			if (this.isSequentialCompetition && this.activeTab === TAB_ATHLETE_TAGS) {
				tags = this.$store.getters['athleteTagFilters/filteredClips'];
			} else {
				tags = this.$store.getters['tagFilters/filteredClips'];
			}

			// Show only timeline=true tags in timeline, unless tags are filtered
			tags = tags.filter(tag => isFiltered || tag.timeline);

			this.setTimelineActiveTags(tags.filter(tag => isFiltered || tag.timeline));
		},
		closeOrClearSearch() {
			if (this.isSequentialCompetition && this.activeTab === TAB_ATHLETE_TAGS) {
				this.isSearchActive = false;
				this.clearAthleteSearch();
			} else {
				if (this.searchKeyword === null) {
					this.isSearchActive = false;
				}
				this.clearSearch();
			}
		},
		onSearchInput(value) {
			if (this.isSequentialCompetition && this.activeTab === TAB_ATHLETE_TAGS) {
				this.searchAthleteClips(value);
			} else {
				this.searchClips(value);
			}
			this.updateTimelineTags();
		},
	},
};
</script>

<style lang="scss" scoped>
@import "@shared/sass/shared-variables";
@import "@videotools/sass/_fonts.scss";

// .tag-list {}

.tag-list__inner-w {
	background-color: var(--one-panel-background-color);
}

.tag-list__footer {
	width: 100%;
}

.tag-list__actions {
	display: flex;
	gap: $sp5;
}

.tag-list__search {
	position: absolute;
	display: flex;
	align-items: center;
	width: 100%;
	height: 100%;
}
</style>
